import { groupBy, throttle } from "lodash";
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useCustomers } from "./hooks/useCustomer";
import {
    useGetDocTypeList,
    useGetDocTypeListShort,
} from "./hooks/useDocTypesData";
import { useAddons, useItemGroups, useItems } from "./hooks/useItemsData";
import { useGetTableOrders } from "./hooks/useOrdersData";
import { useGetPosEntryDetails, useSearchPOSOpening } from "./hooks/usePOSData";
import { useGetUserDefaults } from "./hooks/useUser";
import { AppContext } from "../AppContext";

export const PosContext = createContext();

export const PosProvider = ({ children }) => {
    const {
        data: getPosProfileData,
        isFetched: isPosProfileFetched,
        refetch: refetchPosProfiles,
    } = useGetDocTypeList({
        doctype: "POS Profile",
    });

    const { company, isTabsPos, multipleCompanies, socket } =
        useContext(AppContext);

    const {
        data: tableOrders,
        isFetched,
        refetch: rawRefetchTablesData,
    } = useGetTableOrders({});
    // } = useGetTableOrders({ company: multipleCompanies ? company.name : null });

    // Throttled refetch with promise fan-out to preserve callers using .then()
    const throttledRefetchRef = useRef(null);
    const pendingResolversRef = useRef([]);

    const refetchTablesData = useCallback(() => {
        return new Promise((resolve, reject) => {
            pendingResolversRef.current.push({ resolve, reject });
            if (throttledRefetchRef.current) {
                throttledRefetchRef.current();
            }
        });
    }, []);

    useEffect(() => {
        throttledRefetchRef.current = throttle(() => {
            const settleAll = (fn) => {
                const pending = pendingResolversRef.current.splice(0);
                pending.forEach(({ resolve, reject, value, error }) => fn({ resolve, reject }));
            };
            rawRefetchTablesData()
                .then((result) => {
                    const pending = pendingResolversRef.current.splice(0);
                    pending.forEach(({ resolve }) => resolve(result));
                })
                .catch((error) => {
                    const pending = pendingResolversRef.current.splice(0);
                    pending.forEach(({ reject }) => reject(error));
                });
        }, 1000, { leading: true, trailing: true });

        return () => {
            if (throttledRefetchRef.current && throttledRefetchRef.current.cancel) {
                throttledRefetchRef.current.cancel();
            }
            // Reject any pending promises on unmount
            if (pendingResolversRef.current.length > 0) {
                const pending = pendingResolversRef.current.splice(0);
                pending.forEach(({ reject }) => reject(new Error("PosContext unmounted")));
            }
        };
    }, [rawRefetchTablesData]);
    const { data: getDefaultRoom, refetch: refetchDefaultRoom } =
        useGetUserDefaults("Room");

    const {
        data: menuItems,
        isFetched: isMenuItemsFetched,
        refetch: refetchMenuItems,
    } = useItems();

    useEffect(() => {
        refetchMenuItems();
    }, []);

    const { data: addonsItems } = useAddons();
    const { data: groupedItems } = useItemGroups();

    const { data: customers, refetch: refetchCustomers } = useCustomers();

    const { data: todayOpen } = useSearchPOSOpening();
    const [todayPosName, setTodayPosName] = useState(null);
    const { data: posEntryDetails } = useGetPosEntryDetails(todayPosName);

    const [addresses, setAddresses] = useState(null);
    const [territoryOptions, setTerritoryOptions] = useState([""]);
    const [selectedAddress, setSelectedAddress] = useState(null);

    const { data: getAddresses, refetch: refetchAddresses } =
        useGetDocTypeListShort("Address", ["*"]);
    const { data: getTerritoryOptions } = useGetDocTypeListShort("Territory");

    const [floors, setFloors] = useState([]);
    const [selectedFloor, setSelectedFloor] = useState(null);

    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);

    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);

    const [takeawayTable, setTakeawayTable] = useState(null);

    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderItems, setOrderItems] = useState([]);

    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const discountEnabled = true;

    const [isAppLoading, setAppLoading] = useState(true);

    useEffect(() => {
        if (getAddresses) {
            setAddresses(
                groupBy(getAddresses, ({ address_title }) => address_title)
            );
        }
    }, [getAddresses]);

    useEffect(() => {
        if (getTerritoryOptions) {
            const tmpTerritory = [...getTerritoryOptions];
            setTerritoryOptions(tmpTerritory.map((i) => i.name));
        }
    }, [getTerritoryOptions]);

    useEffect(() => {
        if (todayOpen) {
            if (todayOpen.length > 0) {
                setTodayPosName(todayOpen[0].name);
            } else {
                setTodayPosName(null);
            }
        }
    }, [todayOpen]);

    useEffect(() => {
        if (selectedOrder) {
            setOrderItems(selectedOrder.entry_items || []);
        } else {
            setOrderItems([]);
        }
    }, [selectedOrder]);

    useEffect(() => {
        if (tableOrders) {
            setFloors(tableOrders.message);
            const floor = Object.values(tableOrders.message).find(
                (i) => !!i.rooms
            ); //-TODO CHANGE TO USER PREFRENCES
            const rooms = floor?.rooms;
            if (rooms) {
                setSelectedFloor(floor);
                setRooms(rooms);
                let room = Object.values(rooms)[0];
                if (
                    getDefaultRoom?.message &&
                    getDefaultRoom.message.length > 0
                ) {
                    room = rooms
                        ? Object.values(rooms).find(
                              (i) => i.name === getDefaultRoom.message[0]
                          )
                        : {};
                }

                // let room = rooms["Main Room"]
                if (!room) {
                    room = Object.values(rooms)[0];
                }
                setTakeawayTable(tableOrders.message.takeaway);
                setSelectedRoom(room);
            }
        }
    }, [tableOrders, getDefaultRoom]);

    useEffect(() => {
        if (selectedFloor) {
            const rooms = selectedFloor.rooms;
            setRooms(rooms);
        }
    }, [selectedFloor]);

    useEffect(() => {
        if (selectedRoom && selectedRoom.tables) {
            setTables(Object.values(selectedRoom.tables));
        } else {
            setTables([]);
        }
    }, [selectedRoom]);

    useEffect(() => {
        if (selectedRoom && isMenuItemsFetched) {
            setAppLoading(false);
        }
    }, [isMenuItemsFetched, selectedRoom]);

    useEffect(() => {
        if (orders && orders.length > 0) {
        } else {
            setSelectedOrder(null);
        }
    }, [orders]);

    const getMenuItem = (item) => {
        if (menuItems) {
            return menuItems.find((i) => i.item_code === item);
        } else {
            return null;
        }
    };

    const changeSelectedTable = (tableName, orderName) => {
        if (isTabsPos) {
            setSelectedTable(tableOrders?.message.takeaway);
            setOrders(Object.values(tableOrders?.message.takeaway.orders));
        } else {
            if (tableName && tables.length > 0) {
                let newTable = {};
                if (tableName === "takeaway") {
                    newTable = takeawayTable; //-TODO CHANGE TO USER PREFRENCES
                } else {
                    newTable = tables.find(
                        (i) => i.tableDescription === tableName
                    );
                }

                setSelectedTable(newTable);
                if (orderName) {
                    setSelectedOrder(newTable.orders[orderName]);
                } else if (newTable && newTable.orders) {
                    setSelectedOrder(Object.values(newTable.orders)[0]);
                }

                if (newTable && newTable.orders) {
                    setOrders(Object.values(newTable.orders));
                }
            } else {
                setOrders(null);
            }
        }
    };

    const setTakeawayOrder = (orderName) => {
        if (orderName) {
            if (takeawayTable) {
                const takeawayOrder = takeawayTable.orders[orderName];
                setSelectedTable(takeawayTable);
                setSelectedOrder(takeawayOrder);
            }
        } else {
            //new takeaway table
            setSelectedTable(takeawayTable);
            setSelectedOrder(null);
        }
    };

    useEffect(() => {
        if (socket) {
            socket.emit("doctype_subscribe", "Table Order");

            socket.on("list_update", (data) => {
                if (data.doctype == "Table Order") {
                    console.log("refetching tables data");
                    refetchTablesData();
                }
            });
        }
    }, [socket]);

    return (
        <PosContext.Provider
            value={{
                tables,
                selectedTable,
                setSelectedTable,
                changeSelectedTable,
                orders,
                tableOrders,
                isAppLoading,
                selectedOrder,
                orderItems,
                menuItems,
                getMenuItem,
                customerContext: [selectedCustomer, setSelectedCustomer],
                setSelectedCustomer,
                selectedFloor,
                selectedRoom,
                takeawayTable,
                customers,
                refetchCustomers,
                setTakeawayOrder,
                addonsItems,
                groupedItems,
                floors,
                floorContext: [selectedFloor, setSelectedFloor],
                roomContext: [selectedRoom, setSelectedRoom],
                floorRooms: rooms,
                todayOpen,
                posEntryDetails,
                refetchTablesData,
                addressContext: [selectedAddress, setSelectedAddress],
                addresses,
                refetchAddresses,
                territoryOptions,
                refetchDefaultRoom,
                posProfiles: getPosProfileData,
                discountEnabled,
                socket,
            }}
        >
            {isFetched && isPosProfileFetched ? children : <div>Loading</div>}
            {/* {children} */}
        </PosContext.Provider>
    );
};
